"use client";

import { useEffect, useRef } from "react";

/**
 * A muted, looping video tile that only plays while it is on screen. Reduced motion keeps the
 * first frame as a still. The URL is played natively, like the case hero video.
 */
export default function CaseImageGridVideo({ url }: { url: string }) {
  const video = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const element = video.current;
    if (!element) return;
    // React renders `muted` as a property only, and browsers block unmuted play().
    element.muted = true;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      element.pause();
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) element.play().catch(() => {});
        else element.pause();
      },
      { threshold: 0.25 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [url]);
  return (
    <video
      ref={video}
      src={url}
      muted
      loop
      playsInline
      preload="metadata"
      aria-hidden="true"
      tabIndex={-1}
    />
  );
}
